import React, {useState, useEffect, useRef} from 'react'
import Icon from 'components/utils/Icon'
import AddButton from 'components/utils/AddButton'
import Modal from 'components/utils/Modal'
import SearchBar from 'components/utils/SearchBar'
import CustomTools from 'components/utils/CustomTools'
import Plans from 'components/main/Plans'
import CoordinateFactors from 'components/menu/CoordinateFactors'

import { formatDateYMD, formatDateTime, getLocation, catchError, exportToExcel, filterData, addActivity } from 'scripts/common'

const Drawings = (props) => {

  const classifyPoint = require("robust-point-in-polygon")

  const forDownload = useRef([])  
  const coordFactor = useRef({n: 0, e: 0}) // for download
  const autoCadFactor = useRef({
    blockSize: '1.5',
    blockRotation: '0',
    labelSize: '1.5',
    labelRotation: '0',
    block: 'Circle',
    includeCoordFactor: true
  }) // for download

  const googleRef = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef([])

  const [fetchedData, setFetchedData] = useState([])
  const [filters, setFilters] = useState([])
  const [isModal, setIsModal] = useState({
    add: false,
    edit: false,
    plan: false,
    google: false,
    loading: true,
    filter: false,
    filterMenu: false,
    filterEdit: false,
    filterPolygon: false,
  })
  const isChanged = useRef(false)
  const [searchValue, setSearchValue] = useState('')

  const [isValidated, setIsValidated] = useState({
    entryby: '',
    entrytime: null,
    entrylat: '',
    entrylng: '',
    entrydevice: '',
    modby: '',
    modtime: null,
    modlat: '',
    modlng: '',
    moddevice: '',
    id: null,
    typeId: null,
    elev: '',
    description: '',
    tool: '',
    color: '',
    width: '',
    line: '',
    lineName: 'solid', // this determines which line is selected, had issues comparing an array for the border
  })

  const clearIsValidated = () => setIsValidated({
    entryby: '',
    entrytime: null,
    entrylat: '',
    entrylng: '',
    entrydevice: '',
    modby: '',
    modtime: null,
    modlat: '',
    modlng: '',
    moddevice: '',
    id: null,
    typeId: null,
    elev: '',
    description: '',
    tool: '',
    color: '',
    width: '',
    line: '',
    lineName: 'solid', // this determines which line is selected, had issues comparing an array for the border
  })

  const fetchCoordFactors = () => {

    fetch('/api/selectCoordFactors', {
      method: 'post',
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        filter: props.filter
      })
    })
    .then(res=>res.json())
    .then(
      (result) => {
        //console.log('result: ' + result)

        if (result.length > 0) {

          let n = props.filter.gradeId === 'RG' ? result[0].rgnf : result[0].pgnf
          let e = props.filter.gradeId === 'RG' ? result[0].rgef : result[0].pgef

          coordFactor.current = {
            n: n === null ? 0 : Number(n),
            e: e === null ? 0 : Number(e)
          }

        } else {

          coordFactor.current = {n: 0, e: 0}

        }

      },
      (error) => {
        console.log('Error: selectCoordFactors --> ' + error)
      }
    )

  }

  const fetchData = () => {

    fetch('/api/selectDrawings', {
      method: 'post',
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        filter: props.filter
      })
    })
    .then(res=>res.json())
    .then(
      (result) => {
        //console.log('result: ' + result)

        setFetchedData(result.map(data => ({...data, n: data.n.split(","), e: data.e.split(","), entrytime: data.entrytime !== null ? formatDateTime(data.entrytime) : data.entrytime,})))

        setIsModal(prevState => ({...prevState, loading: false}))
      },
      (error) => {
        console.log('Error: selectDrawings --> ' + error)
      }
    )

  }

  const fetchFilters = () => {
  
    fetch('/api/selectDrawingFilters', {
      method: 'post',
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        filter: props.filter
      })
    })
    .then(res=>res.json())
    .then(
      (result) => {
        //console.log('result: ' + result)

        setFilters(result)

        //setIsModal(prevState => ({...prevState, filterMenu: false}))

      },
      (error) => {
        catchError(props.filter.jobNumber, props.filter.gradeId, props.component, 'selectDrawingFilters', JSON.stringify(error), props.user.username, props.user.device)

      }
    )

  }

  useEffect(() => {
    if (isModal.google) initMap()
  }, [props.filter, isModal.google, fetchedData])

  useEffect(() => {
    if (isModal.google) updateMap()
  }, [searchValue, isModal.google, fetchedData])

  useEffect(() => {
    fetchCoordFactors()
    fetchData()
    fetchFilters()
  }, [props.filter])

  // Sets the map on all markers in the array.
  const hightlightMarker = (e) => {

    let id = parseInt(e.target.parentNode.getAttribute('data-id'))

    for (let i = 0; i < markerRef.current.length; i++) {
      if (markerRef.current[i].get("id") === id) {

        markerRef.current[i].setIcon('http://maps.google.com/mapfiles/ms/icons/yellow-dot.png')
        markerRef.current[i].setAnimation(window.google.maps.Animation.BOUNCE)
      } else {

        markerRef.current[i].setIcon('http://maps.google.com/mapfiles/ms/icons/blue-dot.png')
        markerRef.current[i].setAnimation(null)
        markerRef.current[i].getVisible(false)
      }

      markerRef.current[i].setMap(mapRef.current);
    }
  }

  const updateMap = (id, visible) => {

    for (let i = 0; i < markerRef.current.length; i++) {
      if (markerRef.current[i].get("id") === id) {

        markerRef.current[i].setVisible(visible)

      }

      markerRef.current[i].setMap(mapRef.current);
    }
  }

  const initMap = () => {

    let data = fetchedData.filter(data => {

      if (data.entrylat !== '' && data.entrylat !== null && data.entrylng !== '' && data.entrylng !== null) {

        let jn = data.jobnumber === null ? '' : data.jobnumber
        let gd = data.gradeid === null ? '' : data.gradeid === 'RG' ? 'Grading' : data.gradeid === 'PG' ? 'Post' : ''
        let presetName = data.presetName === null ? '' : data.presetName
        let description = data.description === null ? '' : data.description
        let tool =  data.presetId === 0 ? data.tool === null ? '' : data.tool : data.presetTool === null ? '' : data.presetTool
        let color =  data.presetId === 0 ? data.color === null ? '' : data.color : data.presetColor === null ? '' : data.presetColor
        let width =  data.presetId === 0 ? data.width === null ? '' : data.width : data.presetWidth === null ? '' : data.presetWidth
        let line =  data.presetId === 0 ? data.line === null ? '' : data.line : data.presetLine === null ? '' : data.presetLine
        let entryby = data.entryby === null ? '' : data.entryby
        let entrytime = data.entrytime === null || data.entrytime === '' ? '' : formatDateYMD(data.entrytime, true)

        return true

      }

      return false


    })

    let google = googleRef.current

    mapRef.current = new window.google.maps.Map(google, {
         mapTypeId: window.google.maps.MapTypeId.ROADMAP
    });

    let infoWindow = new window.google.maps.InfoWindow;

    let marker, i, latLng, pre, number, suf, testNo

    //let i, latLng

    let bounds = new window.google.maps.LatLngBounds();

    //console.log(`locations: ${JSON.stringify(data)}`)

    for (i = 0; i < data.length; i++) {

      // pre = data[i].pre === null ? '' : data[i].pre
      // number = data[i].testno === null ? '' : data[i].testno
      // suf = data[i].suf === null ? '' : data[i].suf
      // testNo = `${pre} ${number} ${suf}`

      marker = new window.google.maps.Marker({
        position: new window.google.maps.LatLng(data[i].entrylat, data[i].entrylng),
        title: '',
        animation: window.google.maps.Animation.DROP,
        map: mapRef.current,
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
        },
        id: data[i].id,
        visible: filterData(data[i], searchValue)
      });

      marker.setMap(mapRef.current)

      markerRef.current.push(marker)

      latLng = new window.google.maps.LatLng(data[i].entrylat, data[i].entrylng);

      bounds.extend(latLng);

      window.google.maps.event.addListener(marker, 'click', function () {
        infoWindow.setContent('<h3>' + this.title + '</h3>');
        infoWindow.open(mapRef.current, this);
      });

    }

    mapRef.current.fitBounds(bounds);

    mapRef.current.setCenter(bounds.getCenter());

  }  

  const hexToRgb = (hex) => {
    console.log(`hex: ${hex}`)
    let hexMod = hex === null ? '#000000' : hex.slice(0,7)
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexMod);
    console.log(`color: ${result}`)
    return result ? `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}` : '0,0,0';   
    
  }

  const downloadForCAD = () => {
  
    async function waitFor() {      

      let autoCadData = forDownload.current

      let txt = ''
      let delta = 1.5 // difference between block and label
      let rotate = Number(autoCadFactor.current.blockRotation)
      let toRad = 0.0174533
      let dx = 0
      let dy = 0
      let factor = autoCadFactor.current

      if (rotate === 0 || rotate === 360 ) {
        dx = 0
        dy = 2 * factor.blockSize
      } else if (rotate === 90 ) {
        dx = 2 * factor.blockSize
        dy = 0
      } else if (rotate === 180 ) {
        dx = 0
        dy = -2 * factor.blockSize
      } else if (rotate === 270 ) {
        dx = -2 * factor.blockSize
        dy = 0
      } else if ((rotate > 0 && rotate < 90) || (rotate > 180 && rotate < 270 )) {
        dx = delta * Math.cos(rotate * toRad)
        dy = delta * Math.sin(rotate * toRad)

      } else if ((rotate > 90 && rotate < 180) || (rotate > 270 && rotate < 360)) {
        dx = delta * Math.cos(rotate * toRad) * -1
        dy = delta * Math.sin(rotate * toRad) * -1
      }

      for (let i=0; i < autoCadData.length; i++) {

        let data = autoCadData[i]
        let result = await Promise.resolve(i);

        // console.log(`
        //   n: ${data.north}
        //   e: ${data.east}
        //   nf: ${coordFactor.current.n}
        //   ef: ${coordFactor.current.e}
        // `)
        
                        

        if (data.presetName === 'Bottom Elev') {          

          //let size = 1.5
          //text --> J=Justify, MC=Middle Center

          let n = factor.includeCoordFactor ? Number(data.n) +  Number(coordFactor.current.n) : Number(data.n)
          let e = factor.includeCoordFactor ? Number(data.e) +  Number(coordFactor.current.e) : Number(data.e)

          txt += `layer n "~Bottom Elevs ${formatDateYMD(new Date())}"\n\n`
          txt += `layer s "~Bottom Elevs ${formatDateYMD(new Date())}"\n\n`
          txt += `color T ${hexToRgb(data.presetColor)}\n`;
          txt += "insert BottomEl\n";
          txt += `${e},${n},0 ${factor.blockSize} ${factor.blockSize} ${factor.blockRotation}\n` // ,z xscale yscale rotate
          txt += `text\n`
          txt += `J\n`
          txt += `MC\n`
          txt += `${e + dx},${n + dy}\n`
          txt += `${factor.labelSize}\n` // size
          txt += `${factor.labelRotation}\n` // rotate
          txt += `${data.elev}\n`

        } else if (data.tool === 'planspencil' || data.tool === 'plansmarker') {

          //txt += `-LINETYPE _LOAD DASHED ACAD.lin _*`
        
          if (data.presetId === 0) {
        
            txt += `layer n "~Custom"\n\n`
            txt += `layer s "~Custom"\n\n`
            txt += `color T ${hexToRgb(data.color)}\n`;
            //if (data.line !== '') {txt += `_-LINETYPE _SET DASHED2\n`} else {txt += `_-LINETYPE _SET CONTINUOUS\n`}
            txt += `_PLINE\n`            

            if (factor.includeCoordFactor) {

              for (let j=0; j < data.n.length; j++ ) {

                txt += `${Number(data.e[j]) +  Number(coordFactor.current.e)},${Number(data.n[j]) +  Number(coordFactor.current.n)}\n`
              
              }

              txt += `\n`

            } else {

              for (let j=0; j < data.n.length; j++ ) {

                txt += `${data.e[j]},${data.n[j]}\n`
              
              }

              txt += `\n`


            }
        
          } else {            
        
            txt += `layer n "~${data.presetName}"\n\n`
            txt += `layer s "~${data.presetName}"\n\n`
            txt += `color T ${hexToRgb(data.presetColor)}\n`;
            //if (data.presetLine !== '') {txt += `_-LINETYPE _SET DASHED2\n`} else {txt += `_-LINETYPE _SET CONTINUOUS\n`}
            txt += `_PLINE\n`

            if (factor.includeCoordFactor) {

              for (let j=0; j < data.n.length; j++ ) {

                txt += `${Number(data.e[j]) +  Number(coordFactor.current.e)},${Number(data.n[j]) +  Number(coordFactor.current.n)}\n`
              
              }

              txt += `\n`

            } else {

              for (let j=0; j < data.n.length; j++ ) {

                txt += `${data.e[j]},${data.n[j]}\n`
              
              }

              txt += `\n`


            }
        
          }
        
        } else {          

          //let size = 1.5
          //text --> J=Justify, MC=Middle Center

          let n = factor.includeCoordFactor ? Number(data.n) +  Number(coordFactor.current.n) : Number(data.n)
          let e = factor.includeCoordFactor ? Number(data.e) +  Number(coordFactor.current.e) : Number(data.e)

          txt += `layer n "~${data.presetName}"\n\n`
          txt += `layer s "~${data.presetName}"\n\n`
          txt += `color T ${hexToRgb(data.presetColor)}\n`;
          txt += `insert ${factor.block}\n`
          txt += `${e},${n},0 ${factor.blockSize} ${factor.blockSize} ${factor.blockRotation}\n` // ,z xscale yscale rotate
          txt += `text\n`
          txt += `J\n`
          txt += `MC\n`
          txt += `${e + dx},${n + dy}\n`
          txt += `${factor.labelSize}\n` // size
          txt += `${factor.labelRotation}\n` // rotate
          txt += `${data.elev}\n`

        }

      }

      txt += ``

      return txt

    }

    //if (props.filter.jobNumber !== '' && props.filter.jobNumber !== null) {

      waitFor().then(res => {

        let element = document.createElement('a')
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(res))
        element.setAttribute('download', `${props.filter.jobNumber} ${props.filter.gradeId} Drawings ${formatDateYMD(new Date())}.scr`)

        element.style.display = 'none'
        document.body.appendChild(element)

        element.click()

        document.body.removeChild(element)

        addActivity('field', props.filter.jobNumber, props.filter.gradeId, props.component, 'download', 'Drawings for AutoCAD', props.user.username)

      })

    //} else {
      //alert('Please select a JN')
    //}

  }

  const validate = (event) => {
    let name = event.target.getAttribute('name')
    let state = event.target.reportValidity()
    let type = event.target.type
    let value = type === 'checkbox' ? event.target.checked : event.target.value

    setIsValidated(prevState => ({...prevState, [name]: state ? value : null}))
  }

  const updateTestFilters = (event) => {
    let name = event.target.getAttribute('name')
    let value = event.target.value

    setFilter(prevState => ({...prevState, [name]: value}))
  }

  const updateAutoCadFactor = (event) => {
    let name = event.target.getAttribute('name')
    let value = event.target.value

    autoCadFactor.current = {...autoCadFactor.current, [name]: value}

  }

  const selectRow = (e) => {

    let tr = e.target.parentNode
    let td = tr.getElementsByTagName('td')
    let i = td[0].textContent

    if (i === '' || i === null) {
      alert('Error: data id not found. Contact an admin.')
    } else {

      //console.log(`data: ${JSON.stringify(fetchedData[i])}`)

      setIsValidated(prevState => ({...prevState,
        entryby: fetchedData[i].entryby,
        entrytime: fetchedData[i].entrytime,
        entrylat: fetchedData[i].entrylat,
        entrylng: fetchedData[i].entrylng,
        entrydevice: fetchedData[i].entrydevice,
        modby: fetchedData[i].modby,
        modtime: fetchedData[i].modtime,
        modlat: fetchedData[i].modlat,
        modlng: fetchedData[i].modlng,
        moddevice: fetchedData[i].moddevice,
        id: fetchedData[i].id
      }))
      openEdit()

    }

  }

  const editDrawing = () => {

    alert('still sorting out the full scope of this component')

    // if (props.user.drawing < 2) {
    //   alert('You do not have the required permission. Contact an admin.')
    // } else if (isValidated.userName === null || isValidated.userName ==='') {
    //     alert("Please provide a user name.");
    // } else if (isValidated.userPW === null || isValidated.userPW ==='') {
    //     alert("Please provide a user password.");
    // } else if (isValidated.userLevel === null || isValidated.userLevel ==='') {
    //     alert("Please provide a user level.");
    // } else  if (isValidated.fullName === null || isValidated.fullName ==='') {
    //     alert("Please provide a full name.");
    // } else if (isValidated.email === null || isValidated.email ==='') {
    //     alert("Please provide an email.");
    // } else if (isValidated.phone === null || isValidated.phone ==='') {
    //     alert("Please a phone number.");
    // } else if (isValidated.userLevel === 'guest' && (isValidated.guestAccess === null || isValidated.guestAccess === '')) {
    //     alert("This user is a guest. Please provide jobs they may access.");
    // } else {
    //
    //   getLocation(function(latlng){
    //
    //     fetch('/api/editUser', {
    //       method: 'post',
    //       headers: {
    //         'Accept': 'application/json, text/plain, */*',
    //         'Content-Type': 'application/json'
    //       },
    //       body: JSON.stringify({
    //         by: props.user.username,
    //         time: formatDateTime(new Date()),
    //         lat: latlng.lat,
    //         lng: latlng.lng,
    //         id: isValidated.id,
    //         userName: isValidated.userName,
    //         userPW: isValidated.userPW,
    //         userLevel: isValidated.userLevel,
    //         fullName: isValidated.fullName,
    //         email: isValidated.email,
    //         phone: isValidated.phone,
    //         guestAccess: isValidated.guestAccess,
    //         status: isValidated.status
    //       })
    //     })
    //     .then(res=>res.json())
    //     .then(
    //       (result) => {
    //         //console.log('result: ' + JSON.stringify(result))
    //
    //         setFetchedData(fetchedData.map(data =>
    //           data.userid === isValidated.id ?
    //           {...data,
    //             modby: props.user.username,
    //             modtime: formatDateTime(new Date()),
    //             modlat: latlng.lat,
    //             modlng: latlng.lng,
    //             username: isValidated.userName,
    //             userpw: isValidated.userPW,
    //             userlevel: isValidated.userLevel,
    //             fullname: isValidated.fullName,
    //             email: isValidated.email,
    //             phone: isValidated.phone,
    //             guestaccess: isValidated.guestAccess,
    //             status: isValidated.status
    //           } :
    //           data
    //         ))
    //
    //         closeModal()
    //         alert('Updated')
    //
    //       },
    //       (error) => {
    //
    //         alert('Error: could not edit user. Contact and admin.')
    //         console.log('Error: editUser --> ' + error)
    //       }
    //     )
    //
    //   })
    //
    // }

  }

  const deleteDrawing = () => {

    if (props.user.drawing < 3) {
      alert('You do not have the required permission. Contact an admin.')
    } else {

      if (window.confirm('If you proceed, this will be deleted. Proceed?')) {

        fetch('/api/deleteDrawing', {
          method: 'post',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: isValidated.id
          })
        })
        .then(res=>res.json())
        .then(
          (result) => {
            //console.log('result: ' + JSON.stringify(result))

            fetchData()
            alert('Deleted.')

          },
          (error) => {

            alert('Error: could not delete drawing. Contact and admin.')
            console.log('Error: deleteDrawing --> ' + error)
          }
        )

      }

    }

  }

  const search = (e) => {
    let value = e.target.value
    setSearchValue(value)
  }

  const clearSearch = () => {
    document.getElementById('searchInput').value = ''
    setSearchValue('')
  }

  const selectFilters = (e) => {

    let i = e.target.parentNode.getAttribute('data-i')

    if (e.target.nodeName === 'I' || e.target.nodeName === 'Button') {
      //do nothing
    } else if (i === '' || i === null) {
      alert('Error: data id not found. Contact an admin.')
    } else {

      createFilterPolygon(filters[i].n, filters[i].e)
      .then(result => {

        setFilter(prevState => ({...prevState,
          id: filters[i].id,
          title: filters[i].title,
          description: filters[i].description,
          elev: filters[i].elev,          
          n: filters[i].n,
          e: filters[i].e,
          polygon: result,
          position: filters[i].position
        }))
  
        setIsModal(prevState => ({...prevState, filterMenu: false}))

      })
      .catch(err => console.log(`err: ${err}`))            

    }

  }

  const createFilterPolygon = async (n, e) => {

    let polygon = []

    // console.log(`
    // n: ${JSON.stringify(n)}
    // e: ${JSON.stringify(e)}
    // `)

    let north
    let east

    if (typeof n === 'string') {

      north = n.split(',')
      east = e.split(',')

    } else {

      north = n
      east = e

    }

    //console.log(`north: ${north}`)

    if (north.length > 0) {

      for (let i=0; i < north.length; i++) {
        //console.log(`before ${i}`)
        let result = await Promise.resolve(i);
        polygon.push([Number(east.[i]), Number(north.[i])])
        //console.log(`after ${i}`)
      }

    }

    //console.log(`polygon: ${JSON.stringify(polygon)}`)

    return polygon

  } 

  const [filter, setFilter] = useState({
      id: '',
      description: '',
      title: '',
      elev: '',
      n: '',
      e: '',
      polygon: [],
      position: -1 // indicates position relative to polygon, see below
    })
  
    // filter.postion
    // -1 if point is contained inside loop
    // 0 if point is on the boundary of loop
    // 1 if point is outside loop

  const updateFilters = (event) => {
    let name = event.target.getAttribute('name')
    let value = event.target.value

    setFilter(prevState => ({...prevState, [name]: value}))
  }

  const selectFilterPolygon = (n,e) => {   
    
    createFilterPolygon(n, e)
      .then(result => {

        setFilter(prevState => ({...prevState,
          n: n,
          e: e,
          polygon: result
        }))

      })
      .catch(err => console.log(`err: ${err}`)) 
    
  }

  const clearFilter = () => {

    setFilter({
      id: '',
      description: '',
      title: '',
      elev: '',
      n: '',
      e: '',
      polygon: [],
      position: -1
    })

  }

  const addFilter = () => {
  
    if (props.filter.jobNumber === '') {
      alert('Please select a JN')
    } else {

      let desc = prompt("Provide a Title")

      if (desc === '') {
        alert('Please provide a Title')
      } else {

        fetch('/api/addDrawingFilter', {
          method: 'post',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            filter: props.filter,
            title: desc,
            info: filter
          })
        })
        .then(res=>res.json())
        .then(
          (result) => {
            //console.log('result: ' + result)

            fetchFilters()            

            addActivity('field', props.filter.jobNumber, props.filter.gradeId, props.component, 'add', 'Drawing filter', props.user.username)

          },
          (error) => {
            catchError(props.filter.jobNumber, props.filter.gradeId, props.component, 'addDrawingFilter', JSON.stringify(error), props.user.username, props.user.device)

          }
        )

      }

    }

  }

  const editFilter = () => {

    if (props.filter.jobNumber === '') {
      alert('Please select a JN')
    } else if (filter.title === '') {
      alert('Please provide a Title')
    } else {

      fetch('/api/editDrawingFilter', {
        method: 'post',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filter: props.filter,
          info: filter
        })
      })
      .then(res=>res.json())
      .then(
        (result) => {
          //console.log('result: ' + result)

          fetchFilters()

          setIsModal(prevState => ({...prevState, filterEdit: false}))

          addActivity('field', props.filter.jobNumber, props.filter.gradeId, props.component, 'edit', 'Drawing filter', props.user.username)

        },
        (error) => {
          catchError(props.filter.jobNumber, props.filter.gradeId, props.component, 'editDrawingFilter', JSON.stringify(error), props.user.username, props.user.device)

        }
      )

    }

  }

  const deleteFilter = (e) => {

    let parent = e.target.nodeName === 'I' ? e.target.parentNode.parentNode.parentNode : e.target.nodeName === 'Button' ? e.target.parentNode.parentNode : e.target.parentNode
    let i = parent.getAttribute('data-i')

    if (window.confirm('If you proceed, this will be deleted. Proceed?')) {

      fetch('/api/deleteDrawingFilter', {
        method: 'post',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: filters[i].id
        })
      })
      .then(res=>res.json())
      .then(
        (result) => {
          //console.log('result: ' + JSON.stringify(result))

          addActivity('field', props.filter.jobNumber, props.filter.gradeId, props.component, 'delete', 'Drawing Filter', props.user.username)

          setFilters(filters.filter(data => data.id !== filters[i].id))

        },
        (error) => {

          alert('Error: could not delete filter. Contact and admin.')
          catchError(props.filter.jobNumber, props.filter.gradeId, props.component, 'deleteDrawingFilter', JSON.stringify(error), props.user.username, props.user.device)

        }
      )

    }

  }

  const missingLocation = () => alert('Missing latitude and longitude.')

  const viewEntryLocation = () => window.open(`https://www.google.com/maps/search/?api=1&query=${isValidated.entrylat},${isValidated.entrylng}`)

  const viewModLocation = () => window.open(`https://www.google.com/maps/search/?api=1&query=${isValidated.modlat},${isValidated.modlng}`)

  const openAdd = () => setIsModal(prevState => ({...prevState, add: true}))

  const openEdit = () => setIsModal(prevState => ({...prevState, edit: true}))

  const openFilterPolygon = () => setIsModal(prevState => ({...prevState, filterPolygon: true}))

  const openAutoCad = () => {

    if (props.filter.jobNumber !== '' && props.filter.jobNumber !== null) {

      setIsModal(prevState => ({...prevState, autocad: true}))

    } else {

      if (window.confirm("You did not select a JN. Proceed?")) {

        setIsModal(prevState => ({...prevState, autocad: true}))

      }

    }

  }

  const closeAutoCad = () => setIsModal(prevState => ({...prevState, autocad: false}))

  const togglePlan = () => setIsModal(prevState => ({...prevState, plan: isModal.plan ? false : true}))

  const toggleGoogle = () => setIsModal(prevState => ({...prevState, google: isModal.google ? false : true}))

  const toggleFilter = () => setIsModal(prevState => ({...prevState, filter: isModal.filter ? false : true}))

  const togglePosition = () => {
    //console.log(`toggle: ${filter.position === -1 ? 1 : -1}`)
    setFilter(prevState => ({...prevState, position: filter.position === -1 ? 1 : -1}))
  }

  const openFilterMenu = () => {

    if (props.filter.jobNumber === '') {
      alert('Please select a JN')
    } else {
      setIsModal(prevState => ({...prevState, filterMenu: true}))
    }

  }

  const openFilterEdit = (e) => {

    let parent = e.target.nodeName === 'I' ? e.target.parentNode.parentNode.parentNode : e.target.nodeName === 'Button' ? e.target.parentNode.parentNode : e.target.parentNode
    let i = parent.getAttribute('data-i')
    
    createFilterPolygon(filters[i].n, filters[i].e)
    .then(result => {

      setFilter({
        id: filters[i].id,
        description: filters[i].description,
        prefix: filters[i].prefix,
        tests: filters[i].tests,
        n: filters[i].n,
        e: filters[i].e,
        polygon: result,
        position: filters[i].position
      })
  
      setIsModal(prevState => ({...prevState, filterEdit: true}))

    })
    .catch(err => console.log(`err: ${err}`))    

  }

  const closeModal = () => {

    if (isChanged.current) {
      if (window.confirm('You have unsaved data. Proceed?')) {
        setIsModal(prevState => ({...prevState, add: false, edit: false}))
        clearIsValidated()
      }
    } else {
      setIsModal(prevState => ({...prevState, add: false, edit: false}))
      clearIsValidated()
    }

  }

  const openCoordFactor = () => {

    if (props.filter.jobNumber === '') {
      alert('Please select a JN')
    } else {
      setIsModal(prevState => ({...prevState, coordFactor: true}))
    }

  }

  const closeCoordFactor = () => setIsModal(prevState => ({...prevState, coordFactor: false}))

  const closeFilterMenu = () => setIsModal(prevState => ({...prevState, filterMenu: false}))

  const closeFilterEdit = () => setIsModal(prevState => ({...prevState, filterEdit: false}))

  const closeFilterPolygon = () => setIsModal(prevState => ({...prevState, filterPolygon: false}))

  const filterDescription = (description) => {

    let arrFilter = filter.description.split(',')

    for (let i=0; i < arrFilter.length; i++) {
      if (arrFilter[i].trim().toLowerCase() === description.trim().toLowerCase() || (arrFilter[i].trim().toLowerCase() === 'none' && description === '')) return true
    }

    return false

  }

  const filterElev = (elev) => {

    let arrFilter = filter.elev.split(',')

    for (let i=0; i < arrFilter.length; i++) {
      if (arrFilter[i].includes('-')) {
        let filter = arrFilter[i]
        let index = filter.indexOf('-')
        let start = filter.substring(0,index)
        let end = filter.substring(index+1)

        if (Number(elev) >= Number(start) && Number(elev) <= Number(end)) {return true}

      } else if (Number(elev) === Number(arrFilter[i])) {
        return true
      }
    }

    return false

  }

  const styleTool = (e) => {
    let value = e.target.getAttribute('data-id')
    setIsValidated(prevState => ({...prevState, tool: value}))
  }

  const styleColor = (e) => {
    let value = e.target.getAttribute('data-id')
    setIsValidated(prevState => ({...prevState, color: value}))
  }

  const styleColorOther = () => alert('under construction')

  const styleWidth = (e) => {
    let value = e.target.getAttribute('data-id')
    setIsValidated(prevState => ({...prevState, width: value}))
  }

  const styleLine = (e) => {

    let name = e.target.getAttribute('data-name') // i struggled with comparing an array for the border, so this was a quick fix
    let value = e.target.getAttribute('data-id')

    setIsValidated(prevState => ({...prevState, line: value, lineName: name}))
  }

  forDownload.current = [] // clear before pushing

  let listOfData = fetchedData.map((data, i) => {

    //let select = "SELECT plansdrawid AS drawId, x, y, n, e, presetid AS presetId, tblplansdraw.tool AS tool, tblplansdraw.color AS color, tblplansdraw.width AS width, tblplansdraw.line AS line, fill, tblplansdraw.description AS description, tblplansdraw.entryby AS entryby, tblplansdraw.entrytime AS entrytime, tblplansdraw.modby AS modby, tblplansdraw.modtime AS modtime,"
    //let selectP = " tblplansdraw_style_preset.description AS presetName, tblplansdraw_style_preset.tool AS presetTool, tblplansdraw_style_preset.color AS presetColor, tblplansdraw_style_preset.width AS presetWidth, tblplansdraw_style_preset.line AS presetLine"

    let jn = data.jobnumber === null ? '' : data.jobnumber
    let gd = data.gradeid === null ? '' : data.gradeid === 'RG' ? 'Grading' : data.gradeid === 'PG' ? 'Post' : ''
    let presetName = data.presetName === null ? '' : data.presetName
    let elev = data.elev === null ? '' : data.elev
    let description = data.description === null ? '' : data.description
    let tool =  data.presetId === 0 ? data.tool === null ? '' : data.tool : data.presetTool === null ? '' : data.presetTool
    let color =  data.presetId === 0 ? data.color === null ? '' : data.color : data.presetColor === null ? '' : data.presetColor
    let width =  data.presetId === 0 ? data.width === null ? '' : data.width : data.presetWidth === null ? '' : data.presetWidth
    let line =  data.presetId === 0 ? data.line === null ? '' : data.line : data.presetLine === null ? '' : data.presetLine
    let entryBy = data.entryby !== null && data.entryby !== '' ? data.entryby : data.tech !== null ? data.tech : ''
    let entryTime = data.entrytime === null ? '' : data.entrytime
    let modBy = data.modby !== null && data.modby !== '' ? data.modby : ''
    let modTime = data.modtime === null ? '' : data.modtime

    //let filter = filterData(data, searchValue)
    //console.log(`filter ${filter}`)

    //if (filter) {

    let coords = [Number(data.e[0]), Number(data.n[0])]     
    
    if (      
      ((filter.description === '' || filterDescription(data.description))) &&
      ((filter.elev === '' || filterElev(data.elev))) &&
      ((filter.n === '' || (filter.polygon !== [] && (classifyPoint(filter.polygon, coords) === 0 || classifyPoint(filter.polygon, coords) === filter.position)))) &&
      filterData(data, searchValue)
    ) {

      forDownload.current.push(data)

      if (isModal.google) updateMap(data.id, true)

      return (
        <tr key={data.id.toString()} data-id={data.id} onClick={selectRow} onMouseOver={hightlightMarker}>
          <td style={{display: 'none'}}>{i}</td>
          {isModal.google ?
            <td>
              {data.entrylat === '' || data.entrylat === null || data.entrylng === '' || data.entrylng === null ?
              <Icon name='wrong_location' color='tomato' /> :
              <Icon name='where_to_vote' color='dodgerblue' />}
            </td> : null
          }
          <td>{jn} {gd}</td>
          <td>{presetName}</td>
          <td>{tool}</td>
          <td style={{backgroundColor: color, width: 50}}></td>
          <td style={{display: 'none'}}>{data.e}</td>
          <td style={{display: 'none'}}>{data.n}</td>
          <td>{elev}</td>
          <td className='wrapText'>{description}</td>
          <td>{entryBy}</td>
          <td>{entryTime}</td>
          <td>{modBy}</td>
          <td>{modTime}</td>
        </tr>
      )

    } else {
      if (isModal.google) updateMap(data.id, false)
    }

  })

  let listOfFilters = filters.map((data, i) => {
  
      let jn = data.jobNumber === null ? '' : data.jobNumber
      let gd = data.gradeId === null ? '' : data.gradeId === 'RG' ? 'Grading' : data.gradeId === 'PG' ? 'Post' : ''
  
      return (
        <tr key={data.id.toString()} data-i={i} onClick={selectFilters}>
          <td>{jn} {gd}</td>
          <td>{data.title}</td>
          <td><Icon name='edit' title='Edit filter' outline={true} onClick={openFilterEdit} /></td>
          <td><Icon name='delete' title='Delete filter' color='tomato' outline={true} onClick={deleteFilter} /></td>
        </tr>
      )
  
    })

  let contentFilterMenu = (
    <div>
      {filters.length === 0 ?
        <p>No filters found</p> :
        <table>

          <thead>
            <tr>
              <th>JN</th>
              <th>Desc</th>
              <th></th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {listOfFilters}
          </tbody>

        </table>
      }
    </div>
  )

  let contentFilterEdit = (
    <div style={{width: '100%', height: '100%', textAlign: 'center'}}>

      <div style={{display: 'inline-block', textAlign: 'right', margin: 10}}>

        <div>
          <label>Title</label>
          <input style={{width: 200}} className='input' type="text" name='title' defaultValue={filter.title} onChange={updateTestFilters} />
        </div>

        <div>
          <label>Desc</label>
          <input style={{width: 200}} className='input' type="text" name='description' defaultValue={filter.description} onChange={updateTestFilters} />
        </div>

        <div>
          <label>Elev</label>
          <input style={{width: 200}} className='input' type="text" name='elev' defaultValue={filter.elev} onChange={updateTestFilters} />
        </div>

      </div>

    </div>
  )

  let contentAutoCad = (
    <div style={{width: '100%', height: '100%', textAlign: 'center'}}>

      <div style={{display: 'inline-block', textAlign: 'right', margin: 10}}>

        <div>

          <span>
            <label className='label' style={{display: 'inline-block', width: 100}}>Block</label>
          </span>

          <span>
            <label className='label' style={{display: 'inline-block', width: 100, marginRight: 10}}>Label</label>
          </span>

        </div>

        <div>

          <span>
            <label className='label'>Enter Size</label>
            <input className='input' style={{width: 100}} className='input' type="text" name='blockSize' onChange={updateAutoCadFactor} />
          </span>

          <span>
            <input className='input' style={{width: 100}} className='input' type="text" name='labelSize' onChange={updateAutoCadFactor} />
          </span>

        </div>

        <div>

          <span>
            <label className='label'>Enter Rotation</label>
            <input className='input' style={{width: 100}} className='input' type="text" name='blockRotation' onChange={updateAutoCadFactor} />
          </span>

          <span>
            <input className='input' style={{width: 100}} className='input' type="text" name='labelRotation' onChange={updateAutoCadFactor} />
          </span>

        </div>

        <div>
          <label className='label'>Select Block</label>
          <select className='select' name='block' onChange={updateAutoCadFactor} required>
            <option value='DensityTest'>DensityTest</option>
            <option value='Circle'>Circle</option>
            <option value='Rectangle'>Rectangle</option>
            <option value='Square'>Square</option>
          </select>
        </div>

        <div style={{marginRight: 10}}>
          <label className='label'>Include Coord Factors?</label>
          <input type='checkbox' name='includeCoordFactor' onChange={updateAutoCadFactor} defaultChecked />
        </div>

        <div style={{margin: 10}}>
          <small>The location of the Block files must be added to 'Support File Search Path'</small>
          <div><small>{`AutoCad Icon --> Options --> Files --> Support File Search Path --> Add T:\CAD Files\Blocks`}</small></div>
        </div>

      </div>

    </div>
  )

  let styleBtn = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    borderRadius: 10,
    border: '1px solid black',
    margin: 5
  }

  return (    
    <>
      {isModal.edit ?
        <Modal
          add={isModal.edit ? editDrawing : null}
          content={
            <CustomTools
              style={isValidated}
              styleTool={styleTool}
              styleColor={styleColor}
              styleColorOther={styleColorOther}
              styleWidth={styleWidth}
              styleLine={styleLine}
              validate={validate}
              who={'drawings'}
            />
          }
          closeModal={closeModal}
        /> : null
      }
      {isModal.coordFactor ? <CoordinateFactors user={props.user} filter={props.filter} close={closeCoordFactor} /> : null}      
      {isModal.filterMenu ? <Modal content={contentFilterMenu} closeModal={closeFilterMenu} isModal={isModal} /> : null}
      {isModal.filterEdit ? <Modal add={editFilter} content={contentFilterEdit} closeModal={closeFilterEdit} isModal={isModal} /> : null}
      {isModal.filterPolygon ? <Plans user={props.user} filter={props.filter} component={props.component} selectLocate={selectFilterPolygon} closePlan={closeFilterPolygon} planMode={'plansFilterPolygon'} modal={true} /> : null}
      {isModal.autocad ? <Modal add={downloadForCAD} content={contentAutoCad} closeModal={closeAutoCad} isModal={isModal} /> : null}
      {!isModal.loading ?

        <div style={{display: 'flex', width: '100%', height: '100%', overflow: 'auto'}}>

          <div style={{margin: 10, flex: isModal.google || isModal.plan ? '0 1 auto' : '1 0 auto', maxWidth: '100%'}}>

            <div style={{display: 'flex', flexFlow: 'column', width: '100%', height: '100%'}}>

              <div style={{width: '100%'}}>

                <Icon name='info' title='Add Coordinate Factors' onClick={openCoordFactor} outline={true} />
                <Icon name='file_download' onClick={exportToExcel} />                
                <Icon name='location_on' color={isModal.google ? 'dodgerblue' : 'gray'} onClick={toggleGoogle} />
                <Icon name='map' color={isModal.plan ? 'dodgerblue' : 'gray'} onClick={togglePlan} />
                <Icon name='hdr_auto' title='Download to AutoCAD' outline={true} onClick={openAutoCad} />
                <Icon name='filter_list' title='Advanced Filter' color={isModal.filter ? 'dodgerblue' : 'gray'} onClick={toggleFilter} />
                <Icon name='refresh' onClick={fetchData} />

              </div>

              <SearchBar search={search} clearSearch={clearSearch} />

              {isModal.filter ?
              
                <>

                  <div style={{display: 'flex', alignItems: 'center'}}>

                    <input style={{width: 200, marginLeft: 10}} className='input' type="text" name='description' placeholder='description' onChange={updateFilters} value={filter.description} />
                    <input style={{width: 200, marginLeft: 10}} className='input' type="text" name='elev' placeholder='elev. i.e. 1-10, 12, 14-17' onChange={updateFilters} value={filter.elev} />
                    
                    {!isModal.preview ?

                      <>
                    
                        <Icon name='map' title='Draw Polygon' onClick={openFilterPolygon} />
                        
                        {filter.polygon.length > 0 ?

                          <>

                            <div style={styleBtn}>                    
                              <Icon name='share_location' outline={true} title='Polygon Found!' />
                              <div style={{display: 'inline-block', margin: 10}}>
                                <small style={{marginRight: 10}}>Inside</small>
                                <label className='switch'>
                                  <input type='checkbox' onClick={togglePosition} defaultChecked={filter.position === -1 ? false : true} />
                                  <span className='slider round'></span>
                                </label>
                                <small style={{margin: 10}}>Outside</small>
                              </div>
                            </div>                       
                            
                          </> : null

                        }

                        <Icon name='close' title='Clear filter' color='tomato' onClick={clearFilter} />
                        <Icon name='save' title='Save filter' onClick={addFilter} outline={true} />
                        <Icon name='list' title='Saved Filters' onClick={openFilterMenu} />

                      </> : null
                  
                    }

                  </div>

                  {isModal.preview ?

                    <div style={{display: 'flex', alignItems: 'center'}}>
                    
                      <Icon name='map' title='Draw Polygon' onClick={openFilterPolygon} />
                      
                      {filter.polygon.length > 0 ?

                        <>

                          <div style={styleBtn}>                    
                            <Icon name='share_location' outline={true} title='Polygon Found!' />
                            <div style={{display: 'inline-block', margin: 10}}>
                              <small style={{marginRight: 10}}>Inside</small>
                              <label className='switch'>
                                <input type='checkbox' onClick={togglePosition} defaultChecked={filter.position === -1 ? false : true} />
                                <span className='slider round'></span>
                              </label>
                              <small style={{margin: 10}}>Outside</small>
                            </div>
                          </div>                       
                          
                        </> : null

                      }

                      <Icon name='close' title='Clear filter' color='tomato' onClick={clearFilter} />
                      <Icon name='save' title='Save filter' onClick={addFilter} outline={true} />
                      <Icon name='list' title='Saved Filters' onClick={openFilterMenu} />

                    </div> : null

                  }
                  
                </> : null

              }

              {fetchedData.length > 0 ?

                <>
                  
                  <small style={{margin: 10}}>{fetchedData.length} Total Records. (If no filters, limit 500)</small>

                  <div style={{margin: 10, flex: '1', overflow: 'auto'}}>

                    <table id="toExcel">

                      <thead>
                        <tr>
                          <th style={{display: 'none'}}>id</th>
                          {isModal.google ? <th></th> : null}
                          <th>JN</th>
                          <th>Preset</th>
                          <th>Tool</th>
                          <th>Color</th>
                          <th style={{display: 'none'}}>E</th>
                          <th style={{display: 'none'}}>N</th>
                          <th>Elev.</th>
                          <th>Descrip.</th>
                          <th>Entry by</th>
                          <th>Entry time</th>
                          <th>Mod by</th>
                          <th>Mod time</th>
                        </tr>
                      </thead>

                      <tbody>
                        {listOfData}
                      </tbody>

                    </table>

                  </div>

                </> :
                <p style={{margin: 10}}>No drawings found.</p>

              }

            </div>

          </div>

          {isModal.google ?

            <div style={{margin: 10, flex: '1 0 auto', overflow: 'auto'}}>
              <div style={{height: '100%', width: '100%'}} ref={googleRef}></div>
            </div> : null

          }

          {isModal.plan ?

            <div style={{margin: 10, flex: '0 1 auto', overflow: 'auto'}}>
              <Plans user={props.user} filter={props.filter} planMode={props.planMode} />
            </div> : null

          }

        </div> :
        <p style={{margin: 10}}>Loading...</p>

      }

    </>
  )

}

export default Drawings
